import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';

@Component({
    selector: 'maps-why-suggested-box',
    standalone: false,
    template: `
        <section class="box" [class.is-expanded]="expanded" aria-label="Why suggested">
            <header>Why They Fit</header>
            <p
                *ngIf="text; else skeleton"
                #textEl
                class="text"
                [class.text--expanded]="expanded || full">
                {{ text }}
            </p>
            <!-- Card mode with a drawer available: "Show more" opens the
                 experience drawer (mockup behavior) instead of expanding in
                 place — card heights stay uniform. -->
            <button
                *ngIf="!full && moreOpensExternal && text && isClamped"
                type="button"
                class="toggle toggle--collapsed"
                (click)="showMore.emit()">
                Show more
            </button>
            <button
                *ngIf="!full && !moreOpensExternal && text && (isClamped || expanded)"
                type="button"
                class="toggle"
                [class.toggle--collapsed]="!expanded"
                [attr.aria-expanded]="expanded"
                (click)="expanded = !expanded">
                {{ expanded ? 'Show less' : 'Show more' }}
            </button>
            <ng-template #skeleton>
                <!-- Failure beats shimmer: an indefinite skeleton after a dead
                     request reads as a hung page (stage incident 2026-07-13). -->
                <ng-container *ngIf="failed; else generating">
                    <p class="unavailable">
                        Summary unavailable —
                        <button type="button" class="retry" (click)="retry.emit()">Retry</button>
                    </p>
                </ng-container>
                <ng-template #generating>
                    <div class="skeleton" aria-hidden="true">
                        <span class="line"></span>
                        <span class="line"></span>
                        <span class="line short"></span>
                    </div>
                    <span class="sr-only">Generating summary…</span>
                </ng-template>
            </ng-template>
        </section>
    `,
    styleUrl: './why-suggested-box.component.scss',
})
export class WhySuggestedBoxComponent implements AfterViewChecked {
    @Input() text: string | null | undefined = null;
    /** True when summary generation gave up (retries exhausted): renders the
     *  retry affordance instead of an indefinite skeleton. */
    @Input() failed = false;
    /** Drawer mode: text renders unclamped with no toggle. */
    @Input() full = false;
    /** Card mode with an experience drawer: "Show more" emits showMore
     *  (drawer open) instead of expanding in place. */
    @Input() moreOpensExternal = false;
    @Output() retry = new EventEmitter<void>();
    @Output() showMore = new EventEmitter<void>();
    @ViewChild('textEl') textEl?: ElementRef<HTMLElement>;

    expanded = false;
    isClamped = false;

    private measuredFor: string | null | undefined = undefined;

    constructor(private readonly cdr: ChangeDetectorRef) {}

    /**
     * Detect whether the clamped text actually overflows, so the "Show more"
     * affordance only appears when there's hidden content. Re-measures when the
     * text input changes (it arrives async, replacing the skeleton).
     */
    ngAfterViewChecked(): void {
        if (this.expanded || this.measuredFor === this.text) {
            return;
        }
        this.measuredFor = this.text;
        const el = this.textEl?.nativeElement;
        const clamped = el ? el.scrollHeight > el.clientHeight + 1 : false;
        if (clamped !== this.isClamped) {
            this.isClamped = clamped;
            this.cdr.detectChanges();
        }
    }
}
